import { Center, Fade, Flex, Spinner, Text, useToast } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useState, useMemo, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useDebounce } from 'use-debounce';
import { States, StatesSchema } from '@duke-hq/svc-api-db/schemas/enums/States.schema';

import { availableStates } from '@duke-hq/svc-api/lib/states';
import { LocationError } from '../Locations/LocationError';
import { SiteList, SiteLocationType } from '../Locations/RegionSites/SiteList';
import { StatesFilter } from '@duke-hq/filters/components/StatesFilter';
import { EnabledFilterSiteLocation } from '@duke-hq/filters/components/EnabledFilterSiteLocation';
import { LocationTypeFilter } from '@duke-hq/filters/components/LocationTypeFilter';
import { SearchSiteLocation } from '@duke-hq/filters/components/SearchSiteLocation';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';

export function ProviderConsultantSiteAccess({
  providerId,
  member,
}: {
  providerId: string | number;
  member: NonNullable<RouterOutputs['providers']['user']['consultantById']>;
}) {
  const [search, setSearch] = useState<string>('');
  const [showFilter, setShowFilter] = useState<string | null>(null);
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const toast = useToast();
  const utilsAdmin = trpcAdmin.useUtils();
  const [debouncedSearch] = useDebounce(search, 500);

  const createUserSiteAccess = trpcAdmin.providers.locations.createUserSiteAccess.useMutation({
    onSuccess: async () => {
      toast({
        title: t`Site access added successfully`,
        status: 'success',
        isClosable: true,
        duration: 3000,
      });
    },
    onError: (error) => {
      toast({
        title: error?.message ?? t`Failed to add site access.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    async onSettled() {
      await Promise.all([
        utilsAdmin.providers.locations.listAccessibleSiteLocations.refetch(),
        utilsAdmin.providers.user.listConsultantsWithPagination.refetch(),
      ]);
    },
  });

  const deleteUserSiteAccess = trpcAdmin.providers.locations.deleteUserSiteAccess.useMutation({
    onSuccess: async () => {
      toast({
        title: t`Site access removed successfully`,
        status: 'success',
        isClosable: true,
        duration: 3000,
      });
    },
    onError: (error) => {
      toast({
        title: error?.message ?? t`Failed to remove site access.`,
        status: 'error',
        isClosable: true,
        duration: 3000,
      });
    },
    async onSettled() {
      await Promise.all([
        utilsAdmin.providers.locations.listAccessibleSiteLocations.refetch(),
        utilsAdmin.providers.user.listConsultantsWithPagination.refetch(),
      ]);
    },
  });

  const toggleSiteAvailability = useCallback(
    (site: SiteLocationType) => {
      if (!providerId) {
        return;
      }
      if (!site.assigned) {
        createUserSiteAccess.mutate({
          providerId,
          consultantId: member.id,
          ...(site.type === 'region' ? { regionId: site.id } : {}),
          ...(site.type === 'site' ? { siteId: site.id } : {}),
        });
      } else {
        deleteUserSiteAccess.mutate({
          providerId,
          consultantId: member.id,
          ...(site.type === 'region' ? { regionId: site.id } : {}),
          ...(site.type === 'site' ? { siteId: site.id } : {}),
        });
      }
    },
    [providerId, member.id, createUserSiteAccess, deleteUserSiteAccess],
  );

  const { data, isPending } = trpcAdmin.providers.locations.listAccessibleSiteLocations.useQuery(
    {
      search: debouncedSearch,
      filters: {
        location:
          locationFilter === 'Sites' ? 'site' : locationFilter === 'Regions' ? 'region' : undefined,
        available:
          showFilter === 'Show enabled' ? true : showFilter === 'Show disabled' ? false : undefined,
        state: (stateFilter as States) ?? undefined,
      },
      providerId,
      consultantId: member.id,
    },
    {
      enabled: Boolean(!!providerId && !!member.id),
    },
  );

  const sites = useMemo(() => {
    if (typeof data === 'undefined') {
      return [];
    }

    return data.map((site) => {
      return {
        assigned: site.assigned,
        id: site.id,
        name: site.name,
        // TODO: move this parse to the API
        state: StatesSchema.safeParse(site.state).data,
        country: null,
        description: site.description,
        type: site.type,
        count: site.count,
      } satisfies SiteLocationType;
    });
  }, [data]);

  if (!providerId) {
    return <LocationError message={t`An error occurred, providerId is required.`} />;
  }

  return (
    <>
      <Flex
        w="full"
        gap={2}
        p={4}
        style={{ position: 'sticky', top: '0', zIndex: '1' }}
        bg="bg.canvas"
      >
        <EnabledFilterSiteLocation
          showFilter={showFilter}
          setShowFilter={setShowFilter}
          options={[
            { value: 'Show enabled', label: t`Show enabled` },
            { value: 'Show disabled', label: t`Show disabled` },
          ]}
          defaultLabel={t`Show all`}
        />
        <LocationTypeFilter
          locationFilter={locationFilter}
          setLocationFilter={setLocationFilter}
          options={[
            { value: 'Sites', label: t`Sites` },
            { value: 'Regions', label: t`Regions` },
          ]}
          defaultLabel={t`Location`}
        />
        <StatesFilter
          stateFilter={stateFilter}
          stateOptions={availableStates}
          setStateFilter={setStateFilter}
        />
        <SearchSiteLocation search={search} setSearch={setSearch} placeholder={t`Search`} />
      </Flex>

      {!isPending && sites?.length === 0 && (
        <Center p={8}>
          <Text textAlign="center">
            <Trans>No sites found</Trans>
          </Text>
        </Center>
      )}

      {Boolean(isPending && !sites) && (
        <Fade
          in={true}
          transition={{
            enter: {
              delay: 0.3,
              duration: 0,
            },
          }}
        >
          <Center p={8}>
            <Spinner size="xl" transitionDelay={'2900ms'} />
          </Center>
        </Fade>
      )}

      {sites ? <SiteList sites={sites} toggleSiteAvailability={toggleSiteAvailability} /> : null}
    </>
  );
}
